import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';
import type { InjectedRouter } from 'react-router';

import gql from 'graphql-tag';
import { compose } from 'recompose';

import connectToRouter from 'js/lib/connectToRouter';

import { Link, Typography2 } from '@coursera/cds-core';
import { Box, StyleSheet, Tab, Tabs, color, css, spacing } from '@coursera/coursera-ui';

import { Heading, Spacer } from 'bundles/enterprise-ui/components';
import ThirdPartyOrganizationsHomeContractsModal from 'bundles/internal-enterprise/components/ThirdPartyOrganizationsHomeContractsModal';
import UploadSamlMetadataModal from 'bundles/internal-enterprise/components/UploadSamlMetadataModal';
import type {
  InternalThirdPartyOrganizationPageQuery as InternalThirdPartyOrganizationPageQueryData,
  InternalThirdPartyOrganizationPageQueryVariables,
} from 'bundles/internal-enterprise/components/__generated__/InternalThirdPartyOrganizationPageQuery';
import * as apiUtils from 'bundles/internal-enterprise/utils/apiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import ThirdPartyOrganizationLogo from 'bundles/page-header/components/ThirdPartyOrganizationLogo';

import _t from 'i18n!nls/internal-enterprise';

const styles = StyleSheet.create({
  container: {
    padding: `${spacing.md} ${spacing.xl}`,
    background: color.white,
    minHeight: '100vh',
  },
});

const tabStyle = { fontSize: '1.25rem' };

export const InternalThirdPartyOrganizationPageQuery = gql`
  query InternalThirdPartyOrganizationPageQuery($slug: String!) {
    ThirdPartyOrganizationsV1Resource {
      bySlug(limit: 1, slug: $slug) {
        elements {
          slug
          id
          loginMethod
          squareLogo
          name
          isReal
          emailDomain
          entityId
        }
      }
    }
  }
`;

type PropsFromCaller = {
  children: JSX.Element;
};
type PropsFromRouter = {
  thirdPartySlug: string;
  pathname: string;
  router: InjectedRouter;
};
type PropsFromGraphql = {
  data: DataValue<InternalThirdPartyOrganizationPageQueryData, InternalThirdPartyOrganizationPageQueryVariables>;
};

type Props = PropsFromCaller & PropsFromRouter & PropsFromGraphql;
type State = {
  isContractsModalOpen: boolean;
  isUploadSamlMetadataModalOpen: boolean;
};

export class InternalThirdPartyOrganizationPage extends React.Component<Props, State> {
  state = {
    isContractsModalOpen: false,
    isUploadSamlMetadataModalOpen: false,
  };

  render() {
    const { data, pathname, children, router } = this.props;
    const { isContractsModalOpen, isUploadSamlMetadataModalOpen } = this.state;
    const thirdPartyOrganization = data.ThirdPartyOrganizationsV1Resource?.bySlug?.elements[0];

    if (!thirdPartyOrganization) {
      return null;
    }

    const tabs = ['data-protection', 'academic-integrity', 'auto-enrollment', 'private-authoring', 'plagiarism-emails'];
    const tabPaths = tabs.map((tab) => `/internal/enterprise/organizations/${thirdPartyOrganization.slug}/${tab}`);
    const selectedTabIndex = tabPaths.indexOf(pathname);

    return (
      <div {...css(styles.container)}>
        <Spacer size={24} />
        <Box alignItems="center">
          <ThirdPartyOrganizationLogo
            thirdPartyOrganization={thirdPartyOrganization}
            logoHeight={80}
            logoWidth={80}
            defaultSize={80}
            preferRectangle={false}
          />
          <Spacer size={24} />
          <Box flexDirection="column">
            <Heading>{thirdPartyOrganization.name}</Heading>
            <Spacer size={12} />
            <small>
              {_t('id:')} {thirdPartyOrganization.id}
            </small>
            <small>
              {_t('slug:')} {thirdPartyOrganization.slug}
            </small>
          </Box>
        </Box>
        <Spacer size={24} />
        <Box>
          <Link
            href={`https://www.coursera.org/o/${thirdPartyOrganization.slug}/admin`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {_t('Client-facing Admin Page')}
          </Link>
          <Spacer size={12} />|<Spacer size={12} />
          <Link
            href={`https://www.coursera.org/o/${thirdPartyOrganization.slug}/admin/ssoconfig`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {_t('SSO Config')}
          </Link>
        </Box>
        <Spacer size={24} />
        <Box>
          <Typography2 component="p" color="supportText">
            Email Domain:
          </Typography2>
          <Spacer size={12} />
          {thirdPartyOrganization.emailDomain ? (
            <Typography2 component="p">{thirdPartyOrganization.emailDomain}</Typography2>
          ) : (
            <Typography2 component="p" color="supportText">
              {_t('not configured')}
            </Typography2>
          )}
        </Box>
        <Box>
          <Typography2 component="p" color="supportText">
            {_t('Login Method:')}
          </Typography2>
          <Spacer size={12} />
          <Typography2 component="p">{thirdPartyOrganization.loginMethod || _t('N/A')}</Typography2>
        </Box>
        <Spacer size={24} />
        <Typography2 component="p" color="supportText">
          {_t('More actions:')}
        </Typography2>
        <Spacer size={12} />
        <Box>
          <Button
            text={_t('Download Saml Metadata')}
            onClick={() => {
              apiUtils.downloadOrgMetadata(thirdPartyOrganization.id);
            }}
          />
          <Spacer size={12} />
          <Button
            text={_t('Upload SAML data')}
            onClick={() => {
              this.setState({ isUploadSamlMetadataModalOpen: true });
            }}
          />
          <Spacer size={12} />
          <Button
            text={_t('Check Contracts')}
            onClick={() => {
              this.setState({ isContractsModalOpen: true });
            }}
          />
          <Box alignItems="center" style={{ marginLeft: 'auto' }}>
            <Typography2 component="p">
              {thirdPartyOrganization.isReal ? 'This is a Real organization' : 'This is a Fake organization'}
            </Typography2>
            <Spacer size={12} />
            <div style={{ transform: 'scale(0.75)' }}>
              {thirdPartyOrganization.isReal ? (
                <Button
                  text={_t('Make it fake')}
                  onClick={async () => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(_t('Are you sure?'))) {
                      await apiUtils.setOrganizationAsFake(thirdPartyOrganization.id);
                      data.refetch();
                    }
                  }}
                />
              ) : (
                <Button
                  text={_t('Make it real')}
                  onClick={async () => {
                    // eslint-disable-next-line no-alert
                    if (window.confirm(_t('Are you sure?'))) {
                      await apiUtils.setOrganizationAsReal(thirdPartyOrganization.id);
                      data.refetch();
                    }
                  }}
                />
              )}
            </div>
          </Box>
        </Box>
        {isContractsModalOpen && (
          <ThirdPartyOrganizationsHomeContractsModal
            closeModal={() => this.setState({ isContractsModalOpen: false })}
            organizationId={thirdPartyOrganization.id}
          />
        )}
        {isUploadSamlMetadataModalOpen && (
          <UploadSamlMetadataModal
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onSubmit={() => {}}
            closeModal={() => this.setState({ isUploadSamlMetadataModalOpen: false })}
            organization={thirdPartyOrganization}
          />
        )}
        <Spacer size={24} />
        <Tabs
          tabsToDisplay={10}
          onChange={(_, index) => {
            router.push({
              pathname: tabPaths[index],
            });
          }}
          initialSelectedIndex={selectedTabIndex}
          selectedIndexFromParent={selectedTabIndex}
        >
          <Tab label={_t('Data / PII Protections')} style={tabStyle} />
          <Tab label={_t('Academic Integrity')} style={tabStyle} />
          {/* eslint-disable-next-line no-restricted-syntax */}
          <Tab label={_t('Course Auto-Enrollments')} style={tabStyle} />
          <Tab label={_t('Private Authoring')} style={tabStyle} />
          <Tab label={_t('Plagiarism Emails')} style={tabStyle} />
        </Tabs>
        {children &&
          React.cloneElement(children, {
            thirdPartyOrganizationId: thirdPartyOrganization.id,
          })}
      </div>
    );
  }
}

export default compose<Props, PropsFromCaller>(
  connectToRouter<PropsFromRouter, PropsFromCaller>((router) => ({
    thirdPartySlug: router.params.thirdPartySlug,
    pathname: router.location.pathname,
    router,
  })),
  graphql<
    PropsFromRouter,
    InternalThirdPartyOrganizationPageQueryData,
    InternalThirdPartyOrganizationPageQueryVariables,
    PropsFromGraphql
  >(InternalThirdPartyOrganizationPageQuery, {
    options: (props) => ({
      variables: { slug: props.thirdPartySlug },
    }),
  })
)(InternalThirdPartyOrganizationPage);
